import { useCallback, useEffect, useState } from 'react';
import { getIn, useFormikContext } from 'formik';
import hash from 'object-hash';

import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import FormHelperText from '@mui/material/FormHelperText';

import { EMPTY_ARRAY } from '@e-socna/empty';
import type { Id, UserRoleId } from '@e-socna/data-layer';

import { CheckboxList } from '../../components/CheckboxList';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useFormikOnNextTick } from '../../hooks/useFormikOnNextTick.hook';

import type { Props } from './types/props.type';
import { InfoAlert } from '../../components/InfoAlert';

export const UserRolesFormControl = ({
  fallbackRoleIds,
  lockedRoleIds = EMPTY_ARRAY,
  scope,
  name,
  required,
}: Readonly<Props>) => {
  const { errors, setFieldTouched, touched, values } =
    useFormikContext<Readonly<{ _id: Id }>>();

  const { setFieldValue: setFieldValueOnNextTick } = useFormikOnNextTick();

  const formUserRoleIds = getIn(values, name) as
    readonly UserRoleId[] | null | undefined;
  const canInherit = !!fallbackRoleIds;
  const mustInherit = canInherit && !formUserRoleIds;

  const checkboxIdPrefix = `${hash(`${values._id}-${name}`)}-`;

  const [checkedRoleIds, setCheckedRoleIds] = useState(formUserRoleIds);

  useEffect(() => {
    setFieldValueOnNextTick(name, checkedRoleIds);
  }, [checkedRoleIds, name, setFieldValueOnNextTick]);

  const { userRoles } = useReferencesContext();

  const relevantUserRoles = userRoles.filter(
    (userRole) =>
      (scope === 'contextual' && userRole.isContextual) ||
      (scope === 'global' && userRole.isGlobal),
  );

  const withoutLocked = useCallback(
    (roleIds: readonly UserRoleId[]) =>
      roleIds.filter((roleId) => !lockedRoleIds.includes(roleId)),
    [lockedRoleIds],
  );

  const customize = useCallback(() => {
    setCheckedRoleIds(fallbackRoleIds && withoutLocked(fallbackRoleIds));
  }, [fallbackRoleIds, setCheckedRoleIds, withoutLocked]);

  const reinitialize = useCallback(() => {
    setCheckedRoleIds(undefined);
  }, [setCheckedRoleIds]);

  return (
    <FormControl
      component="fieldset"
      error={!!(getIn(errors, name) && getIn(touched, name))}
      required={required}
      sx={{ ml: 3, display: 'flex' }}
      variant="standard"
    >
      {canInherit ? (
        <InfoAlert
          action={
            <>
              <Button
                color="inherit"
                onClick={mustInherit ? customize : reinitialize}
                size="small"
              >
                {mustInherit ? 'Spécifier' : 'Réinitialiser'}
              </Button>
            </>
          }
          sx={{ display: 'flex', flex: 1, flexGrow: 1, mt: 1 }}
        >
          {mustInherit
            ? 'Les rôles e-SOCNA sont pour le moment hérités.'
            : 'Les rôles e-SOCNA peuvent être hérités.'}
        </InfoAlert>
      ) : null}

      <FormLabel component="legend">Rôles e-SOCNA</FormLabel>
      <CheckboxList<UserRoleId>
        idPrefix={checkboxIdPrefix}
        items={relevantUserRoles.map((item) => ({
          id: item._id,
          label: item.label,
          disabled: lockedRoleIds.includes(item._id),
        }))}
        // eslint-disable-next-line react/jsx-no-bind
        onChange={(ids) => {
          setCheckedRoleIds(withoutLocked(ids));
          void setFieldTouched(name, true, false);
        }}
        value={[
          ...new Set([
            ...((mustInherit ? fallbackRoleIds : checkedRoleIds) ??
              EMPTY_ARRAY),
            ...lockedRoleIds,
          ]),
        ]}
        variant={mustInherit ? 'consultation' : 'edition'}
      />
      {!!(getIn(errors, name) && getIn(touched, name)) && (
        <FormHelperText>{getIn(errors, name)}</FormHelperText>
      )}
      {lockedRoleIds.length > 0 && (
        <FormHelperText>
          Les rôles grisés sont donnés par le workflow.
        </FormHelperText>
      )}
    </FormControl>
  );
};
